// apps/client/src/components/dashboard/CommissionPnlTable.tsx
// ==========================================================================
// COMMISSION P&L TABLE VIEW — Presentation only, no business logic
// ==========================================================================
// Displays the same data as the charts view in tabular format.
// All data comes from existing hooks (useMonthlyAggregates, useMonthlySettlements,
// useLineOfTrailKpi) — no new queries.
//
// Reuses sort/totals patterns from MonthlyTableDashboardPage.tsx.
// ==========================================================================

import React, { useState, useMemo, useRef, useLayoutEffect } from 'react';
import { formatCurrency, formatNumber } from '../../utils/formatters';
import { Spinner } from '../common/Spinner';
import type { RelativePeriod } from '../../types';

/**
 * Columns a drill-down row may not be able to state on the parent's basis.
 *
 * `loanBookCents` and `lot` come from the per-dimension LOT snapshot. When no
 * snapshot exists for that office/template/entity month there is NO honest
 * value: the previous code substituted a SUM of the breakdown's own
 * `lineOfTrail` field, which is a different metric and rendered 4,243 under a
 * parent reading 3,320.
 *
 * `settledCents` and `settlementCount` are settlement-grain on the month row
 * (keyed by settlement_date) and period-grain in the breakdown (keyed by the
 * payment run), so the breakdown's numbers contradict the parent they nest
 * under - 149 loans / $55.3M against 6 / $261,547 on My Mortgage Freedom's Jul
 * 2026. They are null on child rows until a settlement-grain breakdown exists.
 *
 * null means "cannot be stated on this row's basis" and renders as a dash.
 */
export interface OfficeRow {
  officeId: string;
  officeName: string;
  upfrontCents: number;
  trailCents: number;
  totalCents: number;
  clawbackCents: number;
  loanBookCents: number | null;
  lot: number | null;
  settledCents: number | null;
  settlementCount: number | null;
  templates: TemplateRow[];
}

export interface EntityRow {
  entityCode: string;
  entityName: string;
  upfrontCents: number;
  trailCents: number;
  totalCents: number;
  clawbackCents: number;
  loanBookCents: number | null;
  lot: number | null;
  settledCents: number | null;
  settlementCount: number | null;
}

export interface TemplateRow {
  templateId: string;
  templateName: string;
  upfrontCents: number;
  trailCents: number;
  totalCents: number;
  clawbackCents: number;
  loanBookCents: number | null;
  lot: number | null;
  settledCents: number | null;
  settlementCount: number | null;
  entities?: EntityRow[];
}

export interface TableRow {
  yearMonth: string;
  label: string;
  upfrontCents: number;
  trailCents: number;
  totalCents: number;
  clawbackCents: number;
  loanBookCents: number;
  lot: number;
  settledCents: number;
  settlementCount: number;
  offices?: OfficeRow[]; // Phase 2: drill-down breakdown
}

interface CommissionPnlTableProps {
  data: TableRow[];
  loading: boolean;
  hideCommissions: boolean;
  /**
   * Portfolio totals switch. When true alongside `hideCommissions`, the loan
   * VOLUME column (`loanBookCents`) keeps rendering while every commission
   * column stays em-dashed. Defaults to false, i.e. today's behaviour.
   */
  showLoanTotals?: boolean;
  period: RelativePeriod;
  // Phase 2+3: drill-down props (optional for backward compat)
  expandedYears?: Set<string>;
  expandedMonths?: Set<string>;
  expandedOffices?: Set<string>;
  expandedTemplates?: Set<string>;
  onToggleYear?: (year: string) => void;
  onToggleMonth?: (yearMonth: string) => void;
  onToggleOffice?: (key: string) => void;
  onToggleTemplate?: (key: string) => void;
  actionsBar?: React.ReactNode; // rendered inside thead for gap-free sticky
  // Deduplicated unique totals from backend (fixes overcounting across months)
  uniqueTotals?: {
    settlementCount: number;
    settledCents: number;
    byYear?: Record<string, { count: number; cents: number }>;
  };
}

type SortField = 'period' | 'upfront' | 'trail' | 'total' | 'mom' | 'clawbacks' | 'loanBook' | 'lot' | 'settled' | 'loans';
type SortDirection = 'asc' | 'desc';

const CommissionPnlTable: React.FC<CommissionPnlTableProps> = ({
  data,
  loading,
  hideCommissions,
  showLoanTotals = false,
  period,
  expandedYears,
  expandedMonths,
  expandedOffices,
  expandedTemplates,
  onToggleYear,
  onToggleMonth,
  onToggleOffice,
  onToggleTemplate,
  actionsBar,
  uniqueTotals,
}) => {
  // Loan VOLUME gate: hidden only when commissions are hidden AND the
  // portfolio totals switch is off. Commission columns keep using
  // `hideCommissions` on its own.
  const hideLoanVolume = hideCommissions && !showLoanTotals;
  const hasDrillDown = !!onToggleMonth;
  const [sortField, setSortField] = useState<SortField>('period');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Measure thead height for pixel-perfect year row sticky positioning
  // thead includes the actions bar row — one sticky block, zero gaps
  const theadRef = useRef<HTMLTableSectionElement>(null);
  const [theadHeight, setTheadHeight] = useState(0);
  useLayoutEffect(() => {
    if (theadRef.current) {
      setTheadHeight(theadRef.current.getBoundingClientRect().height);
    }
  }, [actionsBar]);

  // Calculate MoM% for each row (compare to previous month in chronological order)
  const dataWithMom = useMemo(() => {
    // Sort chronologically first for MoM calculation
    const chronological = [...data].sort((a, b) => a.yearMonth.localeCompare(b.yearMonth));

    return chronological.map((row, index) => {
      let momPercent: number | null = null;
      if (index > 0) {
        const prevTotal = chronological[index - 1].totalCents;
        if (prevTotal !== 0) {
          momPercent = ((row.totalCents - prevTotal) / Math.abs(prevTotal)) * 100;
        }
      }
      return { ...row, momPercent };
    });
  }, [data]);

  // Handle column header click for sorting
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Sort data
  const sortedData = useMemo(() => {
    const sorted = [...dataWithMom];

    sorted.sort((a, b) => {
      let aValue: number | string;
      let bValue: number | string;

      switch (sortField) {
        case 'period':
          aValue = a.yearMonth;
          bValue = b.yearMonth;
          break;
        case 'upfront':
          aValue = a.upfrontCents;
          bValue = b.upfrontCents;
          break;
        case 'trail':
          aValue = a.trailCents;
          bValue = b.trailCents;
          break;
        case 'total':
          aValue = a.totalCents;
          bValue = b.totalCents;
          break;
        case 'mom':
          aValue = a.momPercent ?? -Infinity;
          bValue = b.momPercent ?? -Infinity;
          break;
        case 'clawbacks':
          aValue = a.clawbackCents;
          bValue = b.clawbackCents;
          break;
        case 'loanBook':
          aValue = a.loanBookCents;
          bValue = b.loanBookCents;
          break;
        case 'lot':
          aValue = a.lot;
          bValue = b.lot;
          break;
        case 'settled':
          aValue = a.settledCents;
          bValue = b.settledCents;
          break;
        case 'loans':
          aValue = a.settlementCount;
          bValue = b.settlementCount;
          break;
        default:
          aValue = 0;
          bValue = 0;
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      }
      return sortDirection === 'asc' ? (aValue as number) - (bValue as number) : (bValue as number) - (aValue as number);
    });

    return sorted;
  }, [dataWithMom, sortField, sortDirection]);

  // Phase 3: Group sorted months by year (years always descending)
  const groupedByYear = useMemo(() => {
    const yearMap: Record<string, typeof sortedData> = {};
    for (const row of sortedData) {
      const year = row.yearMonth.substring(0, 4);
      if (!yearMap[year]) yearMap[year] = [];
      yearMap[year].push(row);
    }
    return Object.keys(yearMap)
      .sort((a, b) => b.localeCompare(a))
      .map((year) => ({ year, months: yearMap[year] }));
  }, [sortedData]);

  // Calculate totals for footer
  // Uses deduplicated uniqueTotals for settlement fields (fixes overcounting across months)
  const totals = useMemo(() => {
    const summed = sortedData.reduce(
      (acc, row) => ({
        upfrontCents: acc.upfrontCents + row.upfrontCents,
        trailCents: acc.trailCents + row.trailCents,
        totalCents: acc.totalCents + row.totalCents,
        clawbackCents: acc.clawbackCents + row.clawbackCents,
        settledCents: acc.settledCents + row.settledCents,
        settlementCount: acc.settlementCount + row.settlementCount,
      }),
      { upfrontCents: 0, trailCents: 0, totalCents: 0, clawbackCents: 0, settledCents: 0, settlementCount: 0 }
    );
    return {
      ...summed,
      // Override with deduplicated unique values if available
      settledCents: uniqueTotals?.settledCents ?? summed.settledCents,
      settlementCount: uniqueTotals?.settlementCount ?? summed.settlementCount,
    };
  }, [sortedData, uniqueTotals]);

  // Sort indicator icon (reused pattern from MonthlyTableDashboardPage)
  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return (
        <span className="text-gray-600 ml-1">
          <svg className="w-3 h-3 inline" fill="currentColor" viewBox="0 0 20 20">
            <path d="M5 8l5-5 5 5H5zm0 4l5 5 5-5H5z" />
          </svg>
        </span>
      );
    }
    return (
      <span className="text-blue-400 ml-1">
        {sortDirection === 'asc' ? (
          <svg className="w-3 h-3 inline" fill="currentColor" viewBox="0 0 20 20">
            <path d="M5 12l5-5 5 5H5z" />
          </svg>
        ) : (
          <svg className="w-3 h-3 inline" fill="currentColor" viewBox="0 0 20 20">
            <path d="M5 8l5 5 5-5H5z" />
          </svg>
        )}
      </span>
    );
  };

  // Format month label with year
  const formatPeriodLabel = (yearMonth: string, label: string): string => {
    const [yearStr] = yearMonth.split('-');
    return `${label} ${yearStr}`;
  };

  // A drill-down cell whose value cannot be stated on the parent row's basis
  // renders a dash, never a substitute figure. See the OfficeRow docblock.
  const NO_SNAPSHOT = 'No loan book snapshot for this office, template or entity in this month';
  const NO_SETTLEMENT_GRAIN = 'Settled figures are settlement-month based and are only available on the month row';
  const childCurrency = (cents: number | null | undefined, title: string): React.ReactNode =>
    cents === null || cents === undefined
      ? <span className="text-gray-500" title={title}>{'\u2014'}</span>
      : formatCurrency(cents);
  const childNumber = (value: number | null | undefined, title: string): React.ReactNode =>
    value === null || value === undefined
      ? <span className="text-gray-500" title={title}>{'\u2014'}</span>
      : formatNumber(value);

  // Format MoM% display
  const formatMom = (momPercent: number | null): React.ReactNode => {
    if (momPercent === null) return <span className="text-gray-500">{'\u2014'}</span>;
    const isPositive = momPercent >= 0;
    const color = isPositive ? 'text-emerald-400' : 'text-red-400';
    const arrow = isPositive ? '\u25B2' : '\u25BC';
    return (
      <span className={color}>
        {arrow} {Math.abs(momPercent).toFixed(1)}%
      </span>
    );
  };

  if (loading) {
    return (
      <div className="p-8 text-center text-gray-400">
        <Spinner size="md" label="Loading table data..." />
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-lg shadow-lg relative">
      <div
        className="absolute inset-0 opacity-10"
        style={{ background: 'radial-gradient(circle at 50% 0%, #3b82f640 0%, transparent 70%)' }}
      />

      <div className="relative z-10">
        <table className="w-full text-sm text-left text-gray-300">
          {/* Column Group Headers */}
          <thead ref={theadRef} className="sticky top-0 z-20" style={{ boxShadow: '0 -3rem 0 0 rgb(17 24 39)' }}>
            {/* Actions bar row — inside thead for gap-free sticky */}
            {actionsBar && (
              <tr className="bg-gray-900">
                <th colSpan={10} className="px-0 py-0 bg-gray-900">
                  {actionsBar}
                </th>
              </tr>
            )}
            <tr className="bg-gray-800">
              <th className="px-4 py-1 bg-gray-800" />
              {/* Commission (Flow) — green */}
              <th colSpan={4} className="px-4 py-1 text-center bg-gray-800">
                <div className="border-t-2 border-emerald-500 pt-1">
                  <span className="text-xs text-emerald-400 font-medium uppercase tracking-wider">Commission (Flow)</span>
                </div>
              </th>
              {/* Book (Snapshot) — amber */}
              <th colSpan={2} className="px-4 py-1 text-center bg-gray-800">
                <div className="border-t-2 border-amber-500 pt-1" title="Point-in-time snapshot, not cumulative">
                  <span className="text-xs text-amber-400 font-medium uppercase tracking-wider">Book (Snapshot)</span>
                </div>
              </th>
              {/* Activity (Flow) — cyan */}
              <th colSpan={3} className="px-4 py-1 text-center bg-gray-800">
                <div className="border-t-2 border-cyan-500 pt-1">
                  <span className="text-xs text-cyan-400 font-medium uppercase tracking-wider">Activity (Flow)</span>
                </div>
              </th>
            </tr>
            {/* Column Headers */}
            <tr className="text-xs text-gray-400 uppercase bg-gray-900">
              <th
                className="px-4 py-3 cursor-pointer hover:bg-gray-700/50 whitespace-nowrap sticky left-0 bg-gray-900 z-10"
                onClick={() => handleSort('period')}
              >
                Period <SortIcon field="period" />
              </th>
              <th
                className="px-4 py-3 cursor-pointer hover:bg-gray-700/50 text-right whitespace-nowrap bg-gray-900"
                onClick={() => handleSort('upfront')}
              >
                Upfront <SortIcon field="upfront" />
              </th>
              <th
                className="px-4 py-3 cursor-pointer hover:bg-gray-700/50 text-right whitespace-nowrap bg-gray-900"
                onClick={() => handleSort('trail')}
              >
                Trail <SortIcon field="trail" />
              </th>
              <th
                className="px-4 py-3 cursor-pointer hover:bg-gray-700/50 text-right whitespace-nowrap bg-gray-900"
                onClick={() => handleSort('total')}
              >
                Total <SortIcon field="total" />
              </th>
              <th
                className="px-4 py-3 cursor-pointer hover:bg-gray-700/50 text-right whitespace-nowrap bg-gray-900"
                onClick={() => handleSort('mom')}
              >
                MoM% <SortIcon field="mom" />
              </th>
              <th
                className="px-4 py-3 cursor-pointer hover:bg-gray-700/50 text-right whitespace-nowrap bg-gray-900"
                onClick={() => handleSort('loanBook')}
              >
                Loan Book <SortIcon field="loanBook" />
              </th>
              <th
                className="px-4 py-3 cursor-pointer hover:bg-gray-700/50 text-right whitespace-nowrap bg-gray-900"
                onClick={() => handleSort('lot')}
              >
                LOT <SortIcon field="lot" />
              </th>
              <th
                className="px-4 py-3 cursor-pointer hover:bg-gray-700/50 text-right whitespace-nowrap bg-gray-900"
                onClick={() => handleSort('clawbacks')}
              >
                Clawbacks <SortIcon field="clawbacks" />
              </th>
              <th
                className="px-4 py-3 cursor-pointer hover:bg-gray-700/50 text-right whitespace-nowrap bg-gray-900"
                onClick={() => handleSort('settled')}
              >
                Settled $ <SortIcon field="settled" />
              </th>
              <th
                className="px-4 py-3 cursor-pointer hover:bg-gray-700/50 text-right whitespace-nowrap bg-gray-900"
                onClick={() => handleSort('loans')}
              >
                # Loans <SortIcon field="loans" />
              </th>
            </tr>
          </thead>
            {groupedByYear.map(({ year, months }) => {
              const isYearExpanded = expandedYears?.has(year) ?? true;
              const hasYearToggle = !!onToggleYear;

              // Year subtotals (flow metrics only)
              // Uses deduplicated uniqueTotals.byYear for settlement fields
              const yearUniqueData = uniqueTotals?.byYear?.[year];
              const yearTotals = {
                upfrontCents: months.reduce((s, m) => s + m.upfrontCents, 0),
                trailCents: months.reduce((s, m) => s + m.trailCents, 0),
                totalCents: months.reduce((s, m) => s + m.totalCents, 0),
                clawbackCents: months.reduce((s, m) => s + m.clawbackCents, 0),
                settledCents: yearUniqueData?.cents ?? months.reduce((s, m) => s + m.settledCents, 0),
                settlementCount: yearUniqueData?.count ?? months.reduce((s, m) => s + m.settlementCount, 0),
              };

              return (
                <tbody key={year}>
                  {/* Year-level header row */}
                  <tr className="border-b border-gray-600 bg-gray-900 sticky z-[15]" style={{ top: theadHeight }}>
                    <td
                      className={`px-4 py-3 font-bold text-white whitespace-nowrap sticky left-0 bg-gray-900 z-[16] ${hasYearToggle ? 'cursor-pointer select-none' : ''}`}
                      onClick={hasYearToggle ? () => onToggleYear!(year) : undefined}
                    >
                      {hasYearToggle && (
                        <span className={`inline-block mr-2 transition-transform ${isYearExpanded ? 'rotate-90' : ''}`}>
                          <svg className="w-3.5 h-3.5 inline" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M6 4l8 6-8 6V4z" />
                          </svg>
                        </span>
                      )}
                      {year}
                    </td>
                    <td className="px-4 py-3 text-right text-pink-400 font-semibold">
                      {hideCommissions ? '\u2014' : formatCurrency(yearTotals.upfrontCents)}
                    </td>
                    <td className="px-4 py-3 text-right text-blue-400 font-semibold">
                      {hideCommissions ? '\u2014' : formatCurrency(yearTotals.trailCents)}
                    </td>
                    <td className="px-4 py-3 text-right text-emerald-400 font-semibold">
                      {hideCommissions ? '\u2014' : formatCurrency(yearTotals.totalCents)}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-500">{'\u2014'}</td>
                    <td className="px-4 py-3 text-right text-gray-500">{'\u2014'}</td>
                    <td className="px-4 py-3 text-right text-gray-500">{'\u2014'}</td>
                    <td className="px-4 py-3 text-right text-red-400 font-semibold">
                      {hideCommissions ? '\u2014' : formatCurrency(yearTotals.clawbackCents)}
                    </td>
                    <td className="px-4 py-3 text-right text-cyan-400 font-semibold">
                      {hideCommissions ? '\u2014' : formatCurrency(yearTotals.settledCents)}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-300 font-semibold">
                      {formatNumber(yearTotals.settlementCount)}
                    </td>
                  </tr>

                  {/* Month rows within this year */}
                  {isYearExpanded && months.map((row) => {
                    const isMonthExpanded = expandedMonths?.has(row.yearMonth) ?? false;
                    const hasOffices = row.offices && row.offices.length > 0;

                    return (
                      <React.Fragment key={row.yearMonth}>
                        {/* Month-level row (Level 1) */}
                        <tr className="border-b border-gray-700 hover:bg-gray-700/30">
                          <td
                            className={`pl-6 pr-4 py-3 font-medium text-white whitespace-nowrap sticky left-0 bg-gray-800/90 z-10 ${hasDrillDown && hasOffices ? 'cursor-pointer select-none' : ''}`}
                            onClick={hasDrillDown && hasOffices ? () => onToggleMonth!(row.yearMonth) : undefined}
                          >
                            {hasDrillDown && hasOffices && (
                              <span className={`inline-block mr-2 transition-transform ${isMonthExpanded ? 'rotate-90' : ''}`}>
                                <svg className="w-3 h-3 inline" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M6 4l8 6-8 6V4z" />
                                </svg>
                              </span>
                            )}
                            {row.label}
                          </td>
                          <td className="px-4 py-3 text-right text-pink-400">
                            {hideCommissions ? '\u2014' : formatCurrency(row.upfrontCents)}
                          </td>
                          <td className="px-4 py-3 text-right text-blue-400">
                            {hideCommissions ? '\u2014' : formatCurrency(row.trailCents)}
                          </td>
                          <td className="px-4 py-3 text-right text-emerald-400 font-medium">
                            {hideCommissions ? '\u2014' : formatCurrency(row.totalCents)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {hideCommissions ? '\u2014' : formatMom(row.momPercent)}
                          </td>
                          <td className="px-4 py-3 text-right text-amber-400">
                            {hideLoanVolume ? '\u2014' : formatCurrency(row.loanBookCents)}
                          </td>
                          <td className="px-4 py-3 text-right text-purple-400">
                            {formatNumber(row.lot)}
                          </td>
                          <td className="px-4 py-3 text-right text-red-400">
                            {hideCommissions ? '\u2014' : formatCurrency(row.clawbackCents)}
                          </td>
                          <td className="px-4 py-3 text-right text-cyan-400">
                            {hideCommissions ? '\u2014' : formatCurrency(row.settledCents)}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-300">
                            {formatNumber(row.settlementCount)}
                          </td>
                        </tr>

                        {/* Office-level sub-rows (Level 2) */}
                        {isMonthExpanded && row.offices?.map((office) => {
                          const officeKey = `${row.yearMonth}:${office.officeId}`;
                          const isOfficeExpanded = expandedOffices?.has(officeKey) ?? false;
                          const hasTemplates = office.templates && office.templates.length > 0;

                          return (
                            <React.Fragment key={officeKey}>
                              <tr className="border-b border-gray-700/50 bg-gray-800/60">
                                <td
                                  className={`pl-10 pr-4 py-2 text-gray-300 whitespace-nowrap sticky left-0 bg-gray-800/80 z-10 ${hasTemplates ? 'cursor-pointer select-none' : ''}`}
                                  onClick={hasTemplates && onToggleOffice ? () => onToggleOffice(officeKey) : undefined}
                                >
                                  {hasTemplates && (
                                    <span className={`inline-block mr-2 transition-transform ${isOfficeExpanded ? 'rotate-90' : ''}`}>
                                      <svg className="w-2.5 h-2.5 inline" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M6 4l8 6-8 6V4z" />
                                      </svg>
                                    </span>
                                  )}
                                  {office.officeName}
                                </td>
                                <td className="px-4 py-2 text-right text-pink-400/80 text-xs">
                                  {hideCommissions ? '\u2014' : formatCurrency(office.upfrontCents)}
                                </td>
                                <td className="px-4 py-2 text-right text-blue-400/80 text-xs">
                                  {hideCommissions ? '\u2014' : formatCurrency(office.trailCents)}
                                </td>
                                <td className="px-4 py-2 text-right text-emerald-400/80 text-xs">
                                  {hideCommissions ? '\u2014' : formatCurrency(office.totalCents)}
                                </td>
                                <td className="px-4 py-2 text-right text-gray-500 text-xs">{'\u2014'}</td>
                                <td className="px-4 py-2 text-right text-amber-400/80 text-xs">
                                  {hideLoanVolume ? '\u2014' : childCurrency(office.loanBookCents, NO_SNAPSHOT)}
                                </td>
                                <td className="px-4 py-2 text-right text-purple-400/80 text-xs">
                                  {childNumber(office.lot, NO_SNAPSHOT)}
                                </td>
                                <td className="px-4 py-2 text-right text-red-400/80 text-xs">
                                  {hideCommissions ? '\u2014' : formatCurrency(office.clawbackCents)}
                                </td>
                                <td className="px-4 py-2 text-right text-cyan-400/80 text-xs">
                                  {hideCommissions ? '\u2014' : childCurrency(office.settledCents, NO_SETTLEMENT_GRAIN)}
                                </td>
                                <td className="px-4 py-2 text-right text-gray-400 text-xs">
                                  {childNumber(office.settlementCount, NO_SETTLEMENT_GRAIN)}
                                </td>
                              </tr>

                              {/* Template-level sub-rows (Level 3) */}
                              {isOfficeExpanded && office.templates?.map((tmpl) => {
                                const templateKey = `${officeKey}:${tmpl.templateId}`;
                                const hasEntities = tmpl.entities && tmpl.entities.length > 0;
                                const isTemplateExpanded = expandedTemplates?.has(templateKey) ?? false;

                                return (
                                  <React.Fragment key={templateKey}>
                                    <tr className="border-b border-gray-700/30 bg-gray-800/40">
                                      <td
                                        className={`pl-16 pr-4 py-1.5 text-gray-400 italic whitespace-nowrap sticky left-0 bg-gray-800/60 z-10 text-xs ${hasEntities ? 'cursor-pointer select-none' : ''}`}
                                        onClick={hasEntities && onToggleTemplate ? () => onToggleTemplate(templateKey) : undefined}
                                      >
                                        {hasEntities && (
                                          <span className={`inline-block mr-1.5 transition-transform ${isTemplateExpanded ? 'rotate-90' : ''}`}>
                                            <svg className="w-2 h-2 inline" fill="currentColor" viewBox="0 0 20 20">
                                              <path d="M6 4l8 6-8 6V4z" />
                                            </svg>
                                          </span>
                                        )}
                                        {tmpl.templateName}
                                      </td>
                                      <td className="px-4 py-1.5 text-right text-pink-400/60 text-xs">
                                        {hideCommissions ? '\u2014' : formatCurrency(tmpl.upfrontCents)}
                                      </td>
                                      <td className="px-4 py-1.5 text-right text-blue-400/60 text-xs">
                                        {hideCommissions ? '\u2014' : formatCurrency(tmpl.trailCents)}
                                      </td>
                                      <td className="px-4 py-1.5 text-right text-emerald-400/60 text-xs">
                                        {hideCommissions ? '\u2014' : formatCurrency(tmpl.totalCents)}
                                      </td>
                                      <td className="px-4 py-1.5 text-right text-gray-500 text-xs">{'\u2014'}</td>
                                      <td className="px-4 py-1.5 text-right text-amber-400/60 text-xs">
                                        {hideLoanVolume ? '\u2014' : childCurrency(tmpl.loanBookCents, NO_SNAPSHOT)}
                                      </td>
                                      <td className="px-4 py-1.5 text-right text-purple-400/60 text-xs">
                                        {childNumber(tmpl.lot, NO_SNAPSHOT)}
                                      </td>
                                      <td className="px-4 py-1.5 text-right text-red-400/60 text-xs">
                                        {hideCommissions ? '\u2014' : formatCurrency(tmpl.clawbackCents)}
                                      </td>
                                      <td className="px-4 py-1.5 text-right text-cyan-400/60 text-xs">
                                        {hideCommissions ? '\u2014' : childCurrency(tmpl.settledCents, NO_SETTLEMENT_GRAIN)}
                                      </td>
                                      <td className="px-4 py-1.5 text-right text-gray-500 text-xs">
                                        {childNumber(tmpl.settlementCount, NO_SETTLEMENT_GRAIN)}
                                      </td>
                                    </tr>

                                    {/* Entity-level sub-rows (Level 4) — unified-mode companies only */}
                                    {isTemplateExpanded && tmpl.entities?.map((entity) => (
                                      <tr key={`${templateKey}:${entity.entityCode}`} className="border-b border-gray-700/20 bg-gray-800/25">
                                        <td className="pl-20 pr-4 py-1 text-gray-500 italic whitespace-nowrap sticky left-0 bg-gray-800/35 z-10 text-xs">
                                          {entity.entityName}
                                        </td>
                                        <td className="px-4 py-1 text-right text-pink-400/40 text-xs">
                                          {hideCommissions ? '\u2014' : formatCurrency(entity.upfrontCents)}
                                        </td>
                                        <td className="px-4 py-1 text-right text-blue-400/40 text-xs">
                                          {hideCommissions ? '\u2014' : formatCurrency(entity.trailCents)}
                                        </td>
                                        <td className="px-4 py-1 text-right text-emerald-400/40 text-xs">
                                          {hideCommissions ? '\u2014' : formatCurrency(entity.totalCents)}
                                        </td>
                                        <td className="px-4 py-1 text-right text-gray-500 text-xs">{'\u2014'}</td>
                                        <td className="px-4 py-1 text-right text-amber-400/40 text-xs">
                                          {hideLoanVolume ? '\u2014' : childCurrency(entity.loanBookCents, NO_SNAPSHOT)}
                                        </td>
                                        <td className="px-4 py-1 text-right text-purple-400/40 text-xs">
                                          {childNumber(entity.lot, NO_SNAPSHOT)}
                                        </td>
                                        <td className="px-4 py-1 text-right text-red-400/40 text-xs">
                                          {hideCommissions ? '\u2014' : formatCurrency(entity.clawbackCents)}
                                        </td>
                                        <td className="px-4 py-1 text-right text-cyan-400/40 text-xs">
                                          {hideCommissions ? '\u2014' : childCurrency(entity.settledCents, NO_SETTLEMENT_GRAIN)}
                                        </td>
                                        <td className="px-4 py-1 text-right text-gray-600 text-xs">
                                          {childNumber(entity.settlementCount, NO_SETTLEMENT_GRAIN)}
                                        </td>
                                      </tr>
                                    ))}
                                  </React.Fragment>
                                );
                              })}
                            </React.Fragment>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              );
            })}
          {/* Totals footer */}
          {sortedData.length > 0 && (
            <tfoot className="bg-gray-900/70 font-semibold">
              <tr className="border-t-2 border-gray-600">
                <td className="px-4 py-3 text-white sticky left-0 bg-gray-900/90 z-10">
                  Total ({sortedData.length} months)
                </td>
                <td className="px-4 py-3 text-right text-pink-400">
                  {hideCommissions ? '\u2014' : formatCurrency(totals.upfrontCents)}
                </td>
                <td className="px-4 py-3 text-right text-blue-400">
                  {hideCommissions ? '\u2014' : formatCurrency(totals.trailCents)}
                </td>
                <td className="px-4 py-3 text-right text-emerald-400 font-medium">
                  {hideCommissions ? '\u2014' : formatCurrency(totals.totalCents)}
                </td>
                <td className="px-4 py-3 text-right text-gray-500">
                  {'\u2014'}
                </td>
                <td className="px-4 py-3 text-right text-gray-500">
                  {'\u2014'}
                </td>
                <td className="px-4 py-3 text-right text-gray-500">
                  {'\u2014'}
                </td>
                <td className="px-4 py-3 text-right text-red-400">
                  {hideCommissions ? '\u2014' : formatCurrency(totals.clawbackCents)}
                </td>
                <td className="px-4 py-3 text-right text-cyan-400">
                  {hideCommissions ? '\u2014' : formatCurrency(totals.settledCents)}
                </td>
                <td className="px-4 py-3 text-right text-gray-300">
                  {formatNumber(totals.settlementCount)}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Summary footer */}
      <div className="px-4 py-3 border-t border-gray-700 text-sm text-gray-400 relative z-10">
        <p>
          Showing {sortedData.length} months across {groupedByYear.length} year{groupedByYear.length !== 1 ? 's' : ''}
          {' \u2022 '}
          Click column headers to sort
        </p>
      </div>
    </div>
  );
};

export default CommissionPnlTable;
